import { lookupSeedCard, normalizeScryfallApiCard, suggestClosestCardNames } from './cardCatalog.ts';
import { computeDeckColorIdentity } from './cosmetics.ts';
import { buildCardDefinitionByName, parsePlainTextDeck } from './starterDecks.ts';
import type { CardDefinition, FormatId, ImportedDeckPayload, RngProvider } from './types.ts';

export type ImporterSourceId =
  | 'plain_text'
  | 'arena'
  | 'mtgo_dek'
  | 'cockatrice_cod'
  | 'xmage_dck'
  | 'untap'
  | 'csv'
  | 'json'
  | 'cube'
  | 'ocr'
  | 'deckstr_url'
  | 'moxfield_url'
  | 'archidekt_url'
  | 'mtggoldfish_url'
  | 'tappedout_url'
  | 'deckstats_url'
  | 'deckbox_url'
  | 'aetherhub_url'
  | 'mtgdecks_url'
  | 'scryfall_url'
  | 'cubecobra_url'
  | 'cubetutor_url';

export interface CsvColumnMapping {
  nameCol: string;
  quantityCol?: string;
  setCol?: string;
  collectorNumberCol?: string;
  foilCol?: string;
  boardCol?: string;
  conditionCol?: string;
}

export interface UnresolvedDeckLine {
  lineNumber: number;
  rawLine: string;
  parsedQuantity: number;
  attemptedName: string;
  suggestions: string[];
  board: 'main' | 'commander' | 'sideboard' | 'maybeboard';
}

export interface DeckImportResult {
  source: ImporterSourceId;
  deck: ImportedDeckPayload;
  warnings: string[];
  legalityWarnings: string[];
  unresolvedLines: UnresolvedDeckLine[];
  requiresPasteFallback?: boolean;
  fallbackInstructions?: string;
  detectedCsvColumns?: string[];
  suggestedCsvMapping?: CsvColumnMapping;
  isCube?: boolean;
}

export type HttpFetchAdapter = (
  url: string,
  headers: Record<string, string>
) => Promise<{ status: number; body: string; contentType?: string }>;

export interface DeckImportOptions {
  filename?: string;
  deckName?: string;
  format?: FormatId;
  csvMapping?: CsvColumnMapping;
  forceSource?: ImporterSourceId;
  featureFlags?: Partial<Record<ImporterSourceId, boolean>>;
  fetchAdapter?: HttpFetchAdapter;
  cardResolver?: (name: string, opts?: { setCode?: string; collectorNumber?: string; foil?: boolean }) => CardDefinition | null;
}

export interface DeckImporter {
  readonly id: ImporterSourceId;
  canHandle(rawInput: string, opts?: DeckImportOptions): boolean;
  importDeck(rawInput: string, opts?: DeckImportOptions): Promise<DeckImportResult>;
}

const BASIC_LAND_NAMES = new Set([
  'plains',
  'island',
  'swamp',
  'mountain',
  'forest',
  'wastes',
  'snow-covered plains',
  'snow-covered island',
  'snow-covered swamp',
  'snow-covered mountain',
  'snow-covered forest',
]);

const RELENTLESS_EXEMPT_NAMES = new Set([
  'relentless rats',
  'rat colony',
  'shadowborn apostle',
  'persistent petitioners',
  "dragon's approach",
  'slime against humanity',
  'hare apparent',
  'templar knight',
  'nazgul',
  'nazgûl',
  'seven dwarves',
]);

function resolveCard(
  name: string,
  meta: { setCode?: string; collectorNumber?: string; foil?: boolean } | undefined,
  opts?: DeckImportOptions
): CardDefinition {
  if (opts?.cardResolver) {
    const custom = opts.cardResolver(name, meta);
    if (custom) return custom;
  }
  return buildCardDefinitionByName(name, meta);
}

/**
 * Non-blocking playtester format validation: warns on size, singleton/4-of rules, and color identity,
 * but NEVER blocks loading an experimental or rule-0 brew.
 */
export function validateDeckForFormat(deck: ImportedDeckPayload, format: FormatId = deck.format ?? 'commander'): string[] {
  const warnings: string[] = [];
  const mainTotal = deck.mainboard.reduce((sum, e) => sum + e.quantity, 0);
  const cmdCount = deck.commanders.length;
  const totalPlayable = mainTotal + cmdCount;

  if (format === 'commander') {
    if (cmdCount === 0) {
      warnings.push('Commander deck has no Commander assigned (you can still playtest or move a Legendary creature to Command Zone).');
    } else if (cmdCount > 2) {
      warnings.push(`Commander deck lists ${cmdCount} commanders (standard rules allow 1, or 2 with Partner/Background).`);
    }
    if (totalPlayable !== 100) {
      warnings.push(`Commander deck has ${totalPlayable} playable cards instead of 100 (${mainTotal} mainboard + ${cmdCount} commander).`);
    }
  } else if (format === 'brawl') {
    if (totalPlayable !== 60 && totalPlayable !== 100) {
      warnings.push(`Brawl deck has ${totalPlayable} cards (standard Brawl uses 60 or 100).`);
    }
  } else if (format !== 'custom') {
    if (mainTotal < 60) {
      warnings.push(`${format.toUpperCase()} mainboard has ${mainTotal} cards (minimum is 60).`);
    }
    const sbTotal = deck.sideboard.reduce((sum, e) => sum + e.quantity, 0);
    if (sbTotal > 15) {
      warnings.push(`Sideboard has ${sbTotal} cards (standard Constructed limit is 15).`);
    }
  }

  const isSingleton = format === 'commander' || format === 'brawl';
  const maxCopies = isSingleton ? 1 : format === 'custom' ? 99 : 4;

  const countsByName = new Map<string, number>();
  for (const c of deck.commanders) {
    const k = c.name.toLowerCase();
    countsByName.set(k, (countsByName.get(k) ?? 0) + 1);
  }
  for (const entry of deck.mainboard) {
    const k = entry.card.name.toLowerCase();
    countsByName.set(k, (countsByName.get(k) ?? 0) + entry.quantity);
  }

  for (const [lowerName, count] of countsByName.entries()) {
    if (BASIC_LAND_NAMES.has(lowerName) || RELENTLESS_EXEMPT_NAMES.has(lowerName)) continue;
    if (lowerName === 'seven dwarves' && count <= 7) continue;
    if (lowerName === 'nazgul' || lowerName === 'nazgûl') continue;
    if (count > maxCopies) {
      warnings.push(`"${lowerName}" appears ${count}× (normal ${format} limit is ${maxCopies}×).`);
    }
  }

  if (isSingleton && deck.commanders.length > 0) {
    const cmdIdentity = new Set(computeDeckColorIdentity({ ...deck, mainboard: [], sideboard: [], maybeboard: [] }));
    for (const entry of deck.mainboard) {
      for (const col of entry.card.colors) {
        if (!cmdIdentity.has(col)) {
          warnings.push(`"${entry.card.name}" (${col}) is outside commander color identity [${Array.from(cmdIdentity).join('') || 'C'}].`);
          break;
        }
      }
    }
  }

  return warnings;
}

/**
 * Computes a deterministic deck hash and non-basic card pool signature for Deck Versioning & Deck Eras.
 * Comparing two `nonBasicMap` objects counts exact card swaps (`>=15` non-basic card changes or commander swap = new Era).
 */
export function computeDeckFingerprint(deck: ImportedDeckPayload): {
  exactHash: string;
  commanderSignature: string;
  nonBasicMap: Record<string, number>;
} {
  const cmdSig = deck.commanders
    .map((c) => c.name.toLowerCase().trim())
    .sort()
    .join('|');

  const nonBasicMap: Record<string, number> = {};
  const allEntries: string[] = [`CMD:${cmdSig}`];

  const sortedMain = [...deck.mainboard].sort((a, b) => a.card.name.localeCompare(b.card.name));
  for (const item of sortedMain) {
    const lower = item.card.name.toLowerCase().trim();
    allEntries.push(`${item.quantity}x:${lower}:${item.card.setCode ?? ''}:${item.card.collectorNumber ?? ''}`);
    if (!BASIC_LAND_NAMES.has(lower)) {
      nonBasicMap[lower] = (nonBasicMap[lower] ?? 0) + item.quantity;
    }
  }

  // FNV-1a 32-bit deterministic hash (works identically in browser & Node)
  const canonical = allEntries.join(';');
  let h = 0x811c9dc5;
  for (let i = 0; i < canonical.length; i++) {
    h ^= canonical.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  const exactHash = `dk_${(h >>> 0).toString(16).padStart(8, '0')}`;

  return {
    exactHash,
    commanderSignature: cmdSig,
    nonBasicMap,
  };
}

/**
 * Counts how many non-basic mainboard cards changed between two versions of a deck,
 * and whether a new Deck Era is triggered (`commanderChanged || nonBasicDelta >= 15`).
 */
export function shouldStartNewDeckEra(
  prevDeck: ImportedDeckPayload,
  nextDeck: ImportedDeckPayload
): { startNewEra: boolean; commanderChanged: boolean; nonBasicChanges: number } {
  const prevFp = computeDeckFingerprint(prevDeck);
  const nextFp = computeDeckFingerprint(nextDeck);
  const commanderChanged = prevFp.commanderSignature !== nextFp.commanderSignature;

  const keys = new Set([...Object.keys(prevFp.nonBasicMap), ...Object.keys(nextFp.nonBasicMap)]);
  let diffSum = 0;
  for (const k of keys) {
    diffSum += Math.abs((prevFp.nonBasicMap[k] ?? 0) - (nextFp.nonBasicMap[k] ?? 0));
  }
  // Replacing 15 cards removes 15 old + adds 15 new (diffSum = 30 -> 15 card slots changed)
  const nonBasicChanges = Math.ceil(diffSum / 2);
  return {
    startNewEra: commanderChanged || nonBasicChanges >= 15,
    commanderChanged,
    nonBasicChanges,
  };
}

// ============================================================================
// CSV PARSER & COLUMN MAPPING (ManaBox, Delver Lens, Dragon Shield, TCGplayer, Cardsphere, Moxfield)
// ============================================================================

export function parseCsvRows(csvText: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < csvText.length; i++) {
    const ch = csvText[i];
    const next = csvText[i + 1];

    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        row.push(cell.trim());
        cell = '';
      } else if (ch === '\n' || (ch === '\r' && next === '\n')) {
        if (ch === '\r') i++;
        row.push(cell.trim());
        if (row.some((c) => c.length > 0)) rows.push(row);
        row = [];
        cell = '';
      } else {
        cell += ch;
      }
    }
  }
  row.push(cell.trim());
  if (row.some((c) => c.length > 0)) rows.push(row);
  return rows;
}

export function detectCsvColumns(csvText: string): {
  headers: string[];
  suggestedMapping: CsvColumnMapping;
} {
  const rows = parseCsvRows(csvText);
  const headers = rows[0] ?? [];
  const findCol = (patterns: RegExp[]): string | undefined => {
    for (const pat of patterns) {
      const hit = headers.find((h) => pat.test(h.trim()));
      if (hit) return hit;
    }
    return undefined;
  };

  return {
    headers,
    suggestedMapping: {
      nameCol:
        findCol([/^name$/i, /^card\s*name$/i, /^product\s*name$/i, /^english\s*name$/i, /card/i]) ??
        headers[0] ??
        'Name',
      quantityCol: findCol([/^quantity$/i, /^count$/i, /^qty$/i, /^tradelist\s*count$/i, /^amount$/i]),
      setCol: findCol([/^set\s*code$/i, /^edition\s*code$/i, /^set$/i, /^edition$/i]),
      collectorNumberCol: findCol([/^collector\s*number$/i, /^card\s*number$/i, /^number$/i, /^cn$/i]),
      foilCol: findCol([/^foil$/i, /^finish$/i, /^printing$/i]),
      boardCol: findCol([/^section$/i, /^board$/i, /^zone$/i, /^category$/i]),
      conditionCol: findCol([/^condition$/i, /^grade$/i]),
    },
  };
}

export function importCsvDeck(
  csvText: string,
  opts: DeckImportOptions = {}
): DeckImportResult {
  const rows = parseCsvRows(csvText);
  const { headers, suggestedMapping } = detectCsvColumns(csvText);
  const mapping: CsvColumnMapping = opts.csvMapping ?? suggestedMapping;
  const format = opts.format ?? 'commander';
  const deckName = opts.deckName ?? 'CSV Imported Deck';

  if (rows.length < 2) {
    const emptyDeck: ImportedDeckPayload = {
      name: deckName,
      format,
      mainboard: [],
      commanders: [],
      sideboard: [],
      maybeboard: [],
    };
    return {
      source: 'csv',
      deck: emptyDeck,
      warnings: ['CSV file contained no card rows.'],
      legalityWarnings: [],
      unresolvedLines: [],
      detectedCsvColumns: headers,
      suggestedCsvMapping: suggestedMapping,
    };
  }

  const colIndex = (colName?: string) =>
    colName ? headers.findIndex((h) => h.toLowerCase() === colName.toLowerCase()) : -1;

  const idxName = colIndex(mapping.nameCol);
  const idxQty = colIndex(mapping.quantityCol);
  const idxSet = colIndex(mapping.setCol);
  const idxCn = colIndex(mapping.collectorNumberCol);
  const idxFoil = colIndex(mapping.foilCol);
  const idxBoard = colIndex(mapping.boardCol);

  const mainboard: { card: CardDefinition; quantity: number }[] = [];
  const commanders: CardDefinition[] = [];
  const sideboard: { card: CardDefinition; quantity: number }[] = [];
  const maybeboard: { card: CardDefinition; quantity: number }[] = [];
  const warnings: string[] = [];

  for (let r = 1; r < rows.length; r++) {
    const cols = rows[r];
    const rawName = idxName >= 0 ? (cols[idxName] ?? '').trim() : '';
    if (!rawName) continue;

    const rawQty = idxQty >= 0 ? Number.parseInt(cols[idxQty] ?? '1', 10) : 1;
    const qty = Number.isFinite(rawQty) && rawQty > 0 ? Math.min(99, rawQty) : 1;
    const setCode = idxSet >= 0 && cols[idxSet] ? cols[idxSet].toUpperCase() : undefined;
    const collectorNumber = idxCn >= 0 && cols[idxCn] ? cols[idxCn] : undefined;
    const foilRaw = idxFoil >= 0 ? (cols[idxFoil] ?? '').toLowerCase() : '';
    const foil = foilRaw === 'foil' || foilRaw === 'etched' || foilRaw === 'true' || foilRaw === 'yes' || foilRaw === '1';
    const boardRaw = idxBoard >= 0 ? (cols[idxBoard] ?? '').toLowerCase() : 'main';

    const card = resolveCard(rawName, { setCode, collectorNumber, foil }, opts);
    if (boardRaw.includes('commander')) {
      for (let i = 0; i < qty; i++) commanders.push(card);
    } else if (boardRaw.includes('side')) {
      sideboard.push({ card, quantity: qty });
    } else if (boardRaw.includes('maybe') || boardRaw.includes('consider')) {
      maybeboard.push({ card, quantity: qty });
    } else {
      mainboard.push({ card, quantity: qty });
    }
  }

  const deck: ImportedDeckPayload = {
    name: deckName,
    format,
    mainboard,
    commanders,
    sideboard,
    maybeboard,
  };

  return {
    source: 'csv',
    deck,
    warnings,
    legalityWarnings: validateDeckForFormat(deck, format),
    unresolvedLines: [],
    detectedCsvColumns: headers,
    suggestedCsvMapping: suggestedMapping,
  };
}

// ============================================================================
// XML & CLIENT EXPORT PARSERS (.dek MTGO, .cod Cockatrice, .dck XMage/Forge, Untap.in)
// ============================================================================

function parseXmlAttributes(tag: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  const re = /([A-Za-z0-9_:-]+)\s*=\s*"([^"]*)"/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(tag)) !== null) {
    attrs[m[1].toLowerCase()] = m[2]
      .replace(/&apos;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&');
  }
  return attrs;
}

export function importMtgoDekXml(xml: string, opts: DeckImportOptions = {}): DeckImportResult {
  const format = opts.format ?? 'modern';
  const mainboard: { card: CardDefinition; quantity: number }[] = [];
  const sideboard: { card: CardDefinition; quantity: number }[] = [];
  const commanders: CardDefinition[] = [];

  const cardTagRe = /<Cards\b[^>]*\/?>/gi;
  let match: RegExpExecArray | null;
  while ((match = cardTagRe.exec(xml)) !== null) {
    const attrs = parseXmlAttributes(match[0]);
    const name = (attrs.name ?? '').trim();
    if (!name) continue;
    const qty = Math.max(1, Math.min(99, Number.parseInt(attrs.quantity ?? '1', 10) || 1));
    const isSide = (attrs.sideboard ?? 'false').toLowerCase() === 'true';
    const card = resolveCard(name, undefined, opts);
    if (isSide) {
      sideboard.push({ card, quantity: qty });
    } else {
      mainboard.push({ card, quantity: qty });
    }
  }

  const deck: ImportedDeckPayload = {
    name: opts.deckName ?? 'MTGO .dek Import',
    format,
    mainboard,
    commanders,
    sideboard,
    maybeboard: [],
  };

  return {
    source: 'mtgo_dek',
    deck,
    warnings: [],
    legalityWarnings: validateDeckForFormat(deck, format),
    unresolvedLines: [],
  };
}

export function importCockatriceCodXml(xml: string, opts: DeckImportOptions = {}): DeckImportResult {
  const format = opts.format ?? 'commander';
  const nameMatch = xml.match(/<deckname>([\s\S]*?)<\/deckname>/i);
  const xmlTitle = nameMatch
    ? nameMatch[1].trim().replace(/&apos;/g, "'").replace(/&amp;/g, '&')
    : '';
  const deckName = xmlTitle || opts.deckName || 'Cockatrice Deck';

  const mainboard: { card: CardDefinition; quantity: number }[] = [];
  const sideboard: { card: CardDefinition; quantity: number }[] = [];
  const commanders: CardDefinition[] = [];

  const zoneRe = /<zone\s+name="([^"]+)"[^>]*>([\s\S]*?)<\/zone>/gi;
  let zm: RegExpExecArray | null;
  while ((zm = zoneRe.exec(xml)) !== null) {
    const zoneName = zm[1].toLowerCase();
    const zoneBody = zm[2];
    const cardRe = /<card\b[^>]*\/?>/gi;
    let cm: RegExpExecArray | null;
    while ((cm = cardRe.exec(zoneBody)) !== null) {
      const attrs = parseXmlAttributes(cm[0]);
      const cardName = (attrs.name ?? '').trim();
      if (!cardName) continue;
      const qty = Math.max(1, Math.min(99, Number.parseInt(attrs.number ?? '1', 10) || 1));
      const card = resolveCard(cardName, undefined, opts);
      if (zoneName === 'side' || zoneName === 'sideboard') {
        if (format === 'commander' && qty === 1 && /legendary/i.test(card.typeLine) && commanders.length === 0) {
          commanders.push(card);
        } else {
          sideboard.push({ card, quantity: qty });
        }
      } else if (zoneName === 'commander') {
        commanders.push(card);
      } else if (zoneName === 'main') {
        mainboard.push({ card, quantity: qty });
      }
    }
  }

  const deck: ImportedDeckPayload = {
    name: deckName,
    format,
    mainboard,
    commanders,
    sideboard,
    maybeboard: [],
  };

  return {
    source: 'cockatrice_cod',
    deck,
    warnings: [],
    legalityWarnings: validateDeckForFormat(deck, format),
    unresolvedLines: [],
  };
}

export function importXmageOrForgeDck(text: string, opts: DeckImportOptions = {}): DeckImportResult {
  const format = opts.format ?? 'commander';
  let deckName = opts.deckName ?? 'XMage / Forge Deck';
  const mainboard: { card: CardDefinition; quantity: number }[] = [];
  const sideboard: { card: CardDefinition; quantity: number }[] = [];
  const commanders: CardDefinition[] = [];
  const warnings: string[] = [];

  let section: 'metadata' | 'main' | 'commander' | 'sideboard' = 'main';
  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const secMatch = line.match(/^\[(metadata|main|commander|sideboard|avatar)\]$/i);
    if (secMatch) {
      const s = secMatch[1].toLowerCase();
      if (s === 'metadata') section = 'metadata';
      else if (s === 'commander') section = 'commander';
      else if (s === 'sideboard') section = 'sideboard';
      else section = 'main';
      continue;
    }
    if (section === 'metadata' || /^NAME=/i.test(line)) {
      const nm = line.match(/^Name=(.+)$/i);
      if (nm) deckName = nm[1].trim();
      continue;
    }

    let targetBoard = section;
    let working = line;
    if (/^SB:\s*/i.test(working)) {
      targetBoard = 'sideboard';
      working = working.replace(/^SB:\s*/i, '').trim();
    }

    // XMage format: `4 [LEA:161] Lightning Bolt` or Forge format: `1 Sol Ring|CMM`
    const xmageMatch = working.match(/^(\d+)\s+(?:\[([A-Za-z0-9]+):([A-Za-z0-9★-]+)\]\s+)?(.+?)(?:\|([A-Za-z0-9]+))?$/);
    if (!xmageMatch) {
      warnings.push(`Skipped unrecognized .dck line: "${line}"`);
      continue;
    }
    const qty = Math.max(1, Math.min(99, Number.parseInt(xmageMatch[1], 10)));
    const setCode = (xmageMatch[2] ?? xmageMatch[5])?.toUpperCase();
    const collectorNumber = xmageMatch[3];
    const cardName = xmageMatch[4].trim();
    const card = resolveCard(cardName, { setCode, collectorNumber }, opts);

    if (targetBoard === 'commander') {
      for (let i = 0; i < qty; i++) commanders.push(card);
    } else if (targetBoard === 'sideboard') {
      sideboard.push({ card, quantity: qty });
    } else {
      mainboard.push({ card, quantity: qty });
    }
  }

  const deck: ImportedDeckPayload = {
    name: deckName,
    format,
    mainboard,
    commanders,
    sideboard,
    maybeboard: [],
  };

  return {
    source: 'xmage_dck',
    deck,
    warnings,
    legalityWarnings: validateDeckForFormat(deck, format),
    unresolvedLines: [],
  };
}

// ============================================================================
// FUZZY PLAIN-TEXT & OCR IMPORTER (With Levenshtein Inline Typo Suggestions)
// ============================================================================

export function importFuzzyTextOrOcrDeck(
  text: string,
  opts: DeckImportOptions & { isOcr?: boolean } = {}
): DeckImportResult {
  const format = opts.format ?? 'commander';
  let deckName = opts.deckName ?? (opts.isOcr ? 'Scanned Photo Deck' : 'Imported Deck');
  const mainboard: { card: CardDefinition; quantity: number }[] = [];
  const commanders: CardDefinition[] = [];
  const companions: CardDefinition[] = [];
  const sideboard: { card: CardDefinition; quantity: number }[] = [];
  const maybeboard: { card: CardDefinition; quantity: number }[] = [];
  const warnings: string[] = [];
  const unresolvedLines: UnresolvedDeckLine[] = [];

  let section: 'about' | 'main' | 'commander' | 'companion' | 'sideboard' | 'maybeboard' = 'main';
  const hasExplicitHeaders =
    /^(about|deck|main|mainboard|commander|commanders|companion|sideboard|maybeboard|considering)\s*:?$|^\/\/\s*(main|deck|commander|sideboard|maybeboard)/im.test(
      text
    );
  const lines = text.split(/\r?\n/);

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i].trim();
    if (!line) {
      if (section === 'commander' || section === 'companion' || section === 'about') {
        section = 'main';
      } else if (!hasExplicitHeaders && section === 'main' && mainboard.length > 0) {
        section = 'sideboard';
      }
      continue;
    }

    // Untap.in / comment section markers (`//Main`, `//Sideboard`, `//Commander`)
    if (line.startsWith('//') || line.startsWith('#')) {
      if (/^\/\/\s*(main|deck)/i.test(line)) section = 'main';
      else if (/^\/\/\s*commander/i.test(line)) section = 'commander';
      else if (/^\/\/\s*sideboard/i.test(line)) section = 'sideboard';
      else if (/^\/\/\s*maybeboard/i.test(line)) section = 'maybeboard';
      continue;
    }

    const headerMatch = line.match(
      /^(about|deck|main|mainboard|commander|commanders|companion|sideboard|maybeboard|considering)\s*:?$/i
    );
    if (headerMatch) {
      const h = headerMatch[1].toLowerCase();
      if (h === 'about') section = 'about';
      else if (h === 'deck' || h === 'main' || h === 'mainboard') section = 'main';
      else if (h === 'commander' || h === 'commanders') section = 'commander';
      else if (h === 'companion') section = 'companion';
      else if (h === 'sideboard') section = 'sideboard';
      else section = 'maybeboard';
      continue;
    }

    if (section === 'about') {
      const nm = line.match(/^Name\s+(.+)$/i);
      if (nm) deckName = nm[1].trim();
      continue;
    }

    // OCR noise cleanup (bullet symbols, trailing mana symbols, OCR 1/I/l digit swaps)
    if (opts.isOcr) {
      line = line
        .replace(/^[•·\-*>]+\s*/, '')
        .replace(/\s+\{[WUBRGC0-9/]+\}+$/i, '')
        .trim();
      if (!/^\d/.test(line)) {
        line = `1 ${line}`;
      }
    }

    let boardOverride: 'main' | 'commander' | 'sideboard' | 'maybeboard' | undefined;
    if (/^SB:\s+/i.test(line)) {
      boardOverride = 'sideboard';
      line = line.replace(/^SB:\s+/i, '').trim();
    }
    if (/\[commander(\{top\})?\]|\*CMDR\*/i.test(line)) {
      boardOverride = 'commander';
      line = line.replace(/\[commander(\{top\})?\]/gi, '').replace(/\*CMDR\*/gi, '').trim();
    } else if (/\[maybeboard\]/i.test(line)) {
      boardOverride = 'maybeboard';
      line = line.replace(/\[maybeboard\]/gi, '').trim();
    } else if (/\[sideboard\]/i.test(line)) {
      boardOverride = 'sideboard';
      line = line.replace(/\[sideboard\]/gi, '').trim();
    }

    // Strip Moxfield hashtags `#Ramp #Draw` and foil markers
    const foil = /\*F\*|\*E\*|\(foil\)/i.test(line);
    line = line
      .replace(/#[A-Za-z0-9_-]+/g, '')
      .replace(/\*F\*|\*E\*|\(foil\)/gi, '')
      .trim();

    const lineMatch = line.match(/^(\d+)x?\s+(.+?)(?:\s+\(([A-Za-z0-9]{2,6})\)(?:\s+([A-Za-z0-9★-]+))?)?$/);
    if (!lineMatch) {
      const suggestions = suggestClosestCardNames(line).map((c) => c.name);
      unresolvedLines.push({
        lineNumber: i + 1,
        rawLine: lines[i],
        parsedQuantity: 1,
        attemptedName: line,
        suggestions,
        board: boardOverride ?? (section === 'companion' ? 'sideboard' : section),
      });
      warnings.push(`Line ${i + 1}: Could not parse "${lines[i].trim()}"`);
      continue;
    }

    const qty = Math.max(1, Math.min(99, Number.parseInt(lineMatch[1], 10)));
    const rawCardName = lineMatch[2].trim();
    const setCode = lineMatch[3]?.toUpperCase();
    const collectorNumber = lineMatch[4];

    // Check for close typo against known catalog (e.g., "Sol Rng" -> "Sol Ring")
    const exactSeed = lookupSeedCard(rawCardName);
    let resolvedCard = resolveCard(rawCardName, { setCode, collectorNumber, foil }, opts);

    if (!exactSeed && resolvedCard.scryfallId.startsWith('named:')) {
      const closeHits = suggestClosestCardNames(rawCardName);
      if (closeHits.length > 0 && closeHits[0].name.toLowerCase() !== rawCardName.toLowerCase()) {
        unresolvedLines.push({
          lineNumber: i + 1,
          rawLine: lines[i],
          parsedQuantity: qty,
          attemptedName: rawCardName,
          suggestions: closeHits.map((c) => c.name),
          board: boardOverride ?? (section === 'companion' ? 'sideboard' : section),
        });
        warnings.push(
          `Line ${i + 1}: "${rawCardName}" looks like a typo — did you mean "${closeHits[0].name}"?`
        );
      }
    }

    const targetBoard = boardOverride ?? section;
    if (targetBoard === 'commander') {
      for (let k = 0; k < qty; k++) commanders.push(resolvedCard);
    } else if (targetBoard === 'companion') {
      companions.push(resolvedCard);
      sideboard.push({ card: resolvedCard, quantity: 1 });
    } else if (targetBoard === 'sideboard') {
      sideboard.push({ card: resolvedCard, quantity: qty });
    } else if (targetBoard === 'maybeboard') {
      maybeboard.push({ card: resolvedCard, quantity: qty });
    } else {
      mainboard.push({ card: resolvedCard, quantity: qty });
    }
  }

  const isArena = /^About\b|\bDeck\r?\n/i.test(text);
  const isUntap = /^\/\/Main\b/im.test(text);
  const source: ImporterSourceId = opts.isOcr
    ? 'ocr'
    : isUntap
    ? 'untap'
    : isArena
    ? 'arena'
    : 'plain_text';

  const deck: ImportedDeckPayload = {
    name: deckName,
    format,
    mainboard,
    commanders,
    companions,
    sideboard,
    maybeboard,
  };

  return {
    source,
    deck,
    warnings,
    legalityWarnings: validateDeckForFormat(deck, format),
    unresolvedLines,
  };
}

// ============================================================================
// JSON & CUBE IMPORTERS (Deckstr JSON, Moxfield JSON, Archidekt JSON, Scryfall JSON, Cube Cobra)
// ============================================================================

export function importJsonOrCubeDeck(jsonText: string, opts: DeckImportOptions = {}): DeckImportResult {
  const parsedRaw = JSON.parse(jsonText);
  const data = (Array.isArray(parsedRaw) ? parsedRaw[0] ?? {} : parsedRaw) as Record<string, any>;
  const format = (data.format as FormatId | undefined) ?? opts.format ?? 'commander';

  // 1. Cube Cobra JSON (`{ name, cards: { mainboard: [{ details: { name, set, collector_number } }] } }`)
  if (data.cards && Array.isArray(data.cards.mainboard)) {
    const mainboard: { card: CardDefinition; quantity: number }[] = [];
    const counts = new Map<string, { card: CardDefinition; qty: number }>();
    for (const item of data.cards.mainboard) {
      const nm = String(item?.details?.name ?? item?.cardID ?? item?.name ?? '').trim();
      if (!nm) continue;
      const key = nm.toLowerCase();
      const existing = counts.get(key);
      if (existing) {
        existing.qty += 1;
      } else {
        counts.set(key, {
          card: resolveCard(
            nm,
            {
              setCode: item?.details?.set?.toUpperCase(),
              collectorNumber: item?.details?.collector_number,
              foil: item?.finish === 'Foil',
            },
            opts
          ),
          qty: 1,
        });
      }
    }
    for (const v of counts.values()) {
      mainboard.push({ card: v.card, quantity: v.qty });
    }
    const deck: ImportedDeckPayload = {
      name: String(data.name ?? opts.deckName ?? 'Cube Cobra Pool'),
      format: 'custom',
      mainboard,
      commanders: [],
      sideboard: [],
      maybeboard: [],
    };
    return {
      source: 'cube',
      deck,
      warnings: [],
      legalityWarnings: [],
      unresolvedLines: [],
      isCube: true,
    };
  }

  // 2. Moxfield v2 / v3 API JSON (`boards: { commanders, mainboard }` OR object-map `{ commanders, mainboard }`)
  if (
    (data.boards && typeof data.boards === 'object') ||
    (data.mainboard && typeof data.mainboard === 'object' && !Array.isArray(data.mainboard))
  ) {
    const boardsRoot = data.boards ?? data;
    const readBoard = (boardObj: any): { card: CardDefinition; quantity: number }[] => {
      const cardsMap = boardObj?.cards ?? boardObj ?? {};
      const list: { card: CardDefinition; quantity: number }[] = [];
      for (const entry of Object.values(cardsMap) as any[]) {
        const rawCard = entry?.card ?? entry;
        const name = String(rawCard?.name ?? '').trim();
        if (!name) continue;
        const qty = Math.max(1, Number(entry?.quantity ?? 1));
        const def = rawCard?.scryfall_id
          ? normalizeScryfallApiCard({ ...rawCard, id: rawCard.scryfall_id })
          : resolveCard(name, { setCode: rawCard?.set?.toUpperCase(), collectorNumber: rawCard?.cn }, opts);
        list.push({ card: def, quantity: qty });
      }
      return list;
    };

    const commanders = readBoard(boardsRoot.commanders).flatMap((e) =>
      Array.from({ length: e.quantity }, () => e.card)
    );
    const companions = readBoard(boardsRoot.companions).map((e) => e.card);
    const mainboard = readBoard(boardsRoot.mainboard);
    const sideboard = readBoard(boardsRoot.sideboard);
    const maybeboard = readBoard(boardsRoot.maybeboard);

    const deck: ImportedDeckPayload = {
      name: String(data.name ?? opts.deckName ?? 'Moxfield Deck'),
      format,
      mainboard,
      commanders,
      companions,
      sideboard,
      maybeboard,
    };
    return {
      source: opts.forceSource ?? 'json',
      deck,
      warnings: [],
      legalityWarnings: validateDeckForFormat(deck, format),
      unresolvedLines: [],
    };
  }

  // 3. Archidekt API JSON (`{ name, cards: [{ quantity, categories: ["Commander"], card: { oracleCard: { name } } }] }`)
  if (Array.isArray(data.cards) && data.cards.some((c: any) => c?.card?.oracleCard || c?.categories)) {
    const mainboard: { card: CardDefinition; quantity: number }[] = [];
    const commanders: CardDefinition[] = [];
    const sideboard: { card: CardDefinition; quantity: number }[] = [];
    const maybeboard: { card: CardDefinition; quantity: number }[] = [];

    for (const item of data.cards) {
      const name = String(item?.card?.oracleCard?.name ?? item?.card?.name ?? item?.name ?? '').trim();
      if (!name) continue;
      const qty = Math.max(1, Number(item?.quantity ?? 1));
      const cats = (Array.isArray(item?.categories) ? item.categories : []).map((x: string) =>
        String(x).toLowerCase()
      );
      const card = resolveCard(
        name,
        {
          setCode: item?.card?.edition?.editioncode?.toUpperCase(),
          collectorNumber: item?.card?.collectorNumber,
          foil: item?.modifier === 'Foil',
        },
        opts
      );

      if (cats.includes('commander')) {
        for (let i = 0; i < qty; i++) commanders.push(card);
      } else if (cats.includes('maybeboard')) {
        maybeboard.push({ card, quantity: qty });
      } else if (cats.includes('sideboard')) {
        sideboard.push({ card, quantity: qty });
      } else {
        mainboard.push({ card, quantity: qty });
      }
    }

    const deck: ImportedDeckPayload = {
      name: String(data.name ?? opts.deckName ?? 'Archidekt Deck'),
      format,
      mainboard,
      commanders,
      sideboard,
      maybeboard,
    };
    return {
      source: opts.forceSource ?? 'json',
      deck,
      warnings: [],
      legalityWarnings: validateDeckForFormat(deck, format),
      unresolvedLines: [],
    };
  }

  // 4. Scryfall Deck JSON (`{ name, entries: { commanders: [...], mainboard: [...], sideboard: [...] } }`)
  if (data.entries && typeof data.entries === 'object') {
    const parseScryEntries = (arr: any[]): { card: CardDefinition; quantity: number }[] => {
      if (!Array.isArray(arr)) return [];
      const out: { card: CardDefinition; quantity: number }[] = [];
      for (const item of arr) {
        const name = String(item?.card_digest?.name ?? item?.raw_text ?? '').trim();
        if (!name) continue;
        const qty = Math.max(1, Number(item?.count ?? 1));
        out.push({ card: resolveCard(name, undefined, opts), quantity: qty });
      }
      return out;
    };
    const commanders = parseScryEntries(data.entries.commanders).flatMap((e) =>
      Array.from({ length: e.quantity }, () => e.card)
    );
    const mainboard = parseScryEntries(data.entries.mainboard ?? data.entries.deck);
    const sideboard = parseScryEntries(data.entries.sideboard);
    const maybeboard = parseScryEntries(data.entries.maybeboard);
    const deck: ImportedDeckPayload = {
      name: String(data.name ?? opts.deckName ?? 'Scryfall Deck'),
      format,
      mainboard,
      commanders,
      sideboard,
      maybeboard,
    };
    return {
      source: opts.forceSource ?? 'json',
      deck,
      warnings: [],
      legalityWarnings: validateDeckForFormat(deck, format),
      unresolvedLines: [],
    };
  }

  // 5. Playtable / Deckstr Native JSON (`{ name/title, mainboard/cards, commanders, sideboard, maybeboard }`)
  if (Array.isArray(data.mainboard)) {
    const deck: ImportedDeckPayload = {
      deckId: data.deckId,
      name: String(data.name ?? opts.deckName ?? 'Playtable Deck'),
      format,
      mainboard: data.mainboard.map((m: any) => ({
        card: m.card?.name ? m.card : resolveCard(String(m.name ?? 'Card'), undefined, opts),
        quantity: Math.max(1, Number(m.quantity ?? 1)),
      })),
      commanders: (data.commanders ?? []).map((c: any) =>
        c?.name && c?.imageUrl ? c : resolveCard(String(c?.name ?? c), undefined, opts)
      ),
      companions: data.companions ?? [],
      sideboard: (data.sideboard ?? []).map((s: any) => ({
        card: s.card?.name ? s.card : resolveCard(String(s.name ?? 'Card'), undefined, opts),
        quantity: Math.max(1, Number(s.quantity ?? 1)),
      })),
      maybeboard: (data.maybeboard ?? []).map((m: any) => ({
        card: m.card?.name ? m.card : resolveCard(String(m.name ?? 'Card'), undefined, opts),
        quantity: Math.max(1, Number(m.quantity ?? 1)),
      })),
    };
    return {
      source: opts.forceSource ?? 'json',
      deck,
      warnings: [],
      legalityWarnings: validateDeckForFormat(deck, format),
      unresolvedLines: [],
    };
  }

  // Deckstr Supabase Deck Row (`{ title, format, commander_name, cards: [{ card_name, quantity, board }] }`)
  if (Array.isArray(data.cards)) {
    const mainboard: { card: CardDefinition; quantity: number }[] = [];
    const commanders: CardDefinition[] = [];
    const sideboard: { card: CardDefinition; quantity: number }[] = [];
    const maybeboard: { card: CardDefinition; quantity: number }[] = [];

    if (data.commander_name) {
      commanders.push(resolveCard(String(data.commander_name), undefined, opts));
    }
    if (Array.isArray(data.commander)) {
      for (const cmd of data.commander) {
        const nm = String(cmd?.card_name ?? cmd?.name ?? cmd ?? '').trim();
        if (nm) commanders.push(resolveCard(nm, undefined, opts));
      }
    }
    for (const c of [...data.cards, ...(Array.isArray(data.lands) ? data.lands : [])]) {
      const nm = String(c.card_name ?? c.name ?? '').trim();
      if (!nm) continue;
      const qty = Math.max(1, Number(c.quantity ?? c.count ?? 1));
      const bd = String(c.board ?? c.zone ?? 'main').toLowerCase();
      const card = resolveCard(nm, { setCode: c.set_code?.toUpperCase(), collectorNumber: c.collector_number }, opts);
      if (bd === 'commander') commanders.push(card);
      else if (bd === 'sideboard' || bd === 'side') sideboard.push({ card, quantity: qty });
      else if (bd === 'maybeboard' || bd === 'maybe') maybeboard.push({ card, quantity: qty });
      else mainboard.push({ card, quantity: qty });
    }
    const deck: ImportedDeckPayload = {
      name: String(data.title ?? data.name ?? opts.deckName ?? 'Deckstr Deck'),
      format,
      mainboard,
      commanders,
      sideboard,
      maybeboard,
    };
    return {
      source: opts.forceSource ?? 'deckstr_url',
      deck,
      warnings: [],
      legalityWarnings: validateDeckForFormat(deck, format),
      unresolvedLines: [],
    };
  }

  throw new Error('Unrecognized JSON deck structure.');
}

// ============================================================================
// URL IMPORTER (All 12 Deck & Cube Sites + Feature Flags + Paste Fallback)
// ============================================================================

interface UrlSiteSpec {
  source: ImporterSourceId;
  label: string;
  hostRegex: RegExp;
  buildExportUrl(parsed: URL): { url: string; kind: 'json' | 'text' | 'csv' } | null;
  fallbackHint: string;
}

const URL_SITE_SPECS: UrlSiteSpec[] = [
  {
    source: 'deckstr_url',
    label: 'Deckstr.gg',
    hostRegex: /(^|\.)deckstr\.gg$/i,
    buildExportUrl(u) {
      const m = u.pathname.match(/\/decks\/([A-Za-z0-9_-]+)/i);
      return m ? { url: `${u.origin}/api/decks/${m[1]}`, kind: 'json' } : null;
    },
    fallbackHint: 'On Deckstr.gg, click "Export -> Copy MTGA / Plain Text" and paste below.',
  },
  {
    source: 'moxfield_url',
    label: 'Moxfield',
    hostRegex: /(^|\.)moxfield\.com$/i,
    buildExportUrl(u) {
      const m = u.pathname.match(/\/decks\/([A-Za-z0-9_-]+)/i);
      return m ? { url: `https://api2.moxfield.com/v3/decks/all/${m[1]}`, kind: 'json' } : null;
    },
    fallbackHint: 'On Moxfield, click "More -> Export -> Copy for MTGO/Arena" and paste the list below.',
  },
  {
    source: 'archidekt_url',
    label: 'Archidekt',
    hostRegex: /(^|\.)archidekt\.com$/i,
    buildExportUrl(u) {
      const m = u.pathname.match(/\/decks\/(\d+)/i);
      return m ? { url: `https://archidekt.com/api/decks/${m[1]}/`, kind: 'json' } : null;
    },
    fallbackHint: 'On Archidekt, click "Extras -> Export Deck -> Text" and paste below.',
  },
  {
    source: 'mtggoldfish_url',
    label: 'MTGGoldfish',
    hostRegex: /(^|\.)mtggoldfish\.com$/i,
    buildExportUrl(u) {
      const m = u.pathname.match(/\/deck\/(\d+)/i);
      return m ? { url: `https://www.mtggoldfish.com/deck/download/${m[1]}`, kind: 'text' } : null;
    },
    fallbackHint: 'On MTGGoldfish, click "Export -> Text (Download)" and paste or drop the .txt file below.',
  },
  {
    source: 'tappedout_url',
    label: 'TappedOut',
    hostRegex: /(^|\.)tappedout\.net$/i,
    buildExportUrl(u) {
      const m = u.pathname.match(/\/mtg-decks\/([^/]+)/i);
      return m ? { url: `https://tappedout.net/mtg-decks/${m[1]}/?fmt=txt`, kind: 'text' } : null;
    },
    fallbackHint: 'On TappedOut, click "Download / Export -> MTGO / Text" and paste below.',
  },
  {
    source: 'deckstats_url',
    label: 'Deckstats',
    hostRegex: /(^|\.)deckstats\.net$/i,
    buildExportUrl(u) {
      return { url: `${u.origin}${u.pathname}?include_comments=1&export_txt=1`, kind: 'text' };
    },
    fallbackHint: 'On Deckstats, open "Tools -> Download/Export -> Text" and paste below.',
  },
  {
    source: 'deckbox_url',
    label: 'Deckbox',
    hostRegex: /(^|\.)deckbox\.org$/i,
    buildExportUrl(u) {
      const m = u.pathname.match(/\/sets\/(\d+)/i);
      return m ? { url: `https://deckbox.org/sets/${m[1]}/export`, kind: 'text' } : null;
    },
    fallbackHint: 'On Deckbox, click "Tools -> Export" and paste the plain list below.',
  },
  {
    source: 'aetherhub_url',
    label: 'AetherHub',
    hostRegex: /(^|\.)aetherhub\.com$/i,
    buildExportUrl(u) {
      const m = u.pathname.match(/\/Deck\/(?:Public|Edit)\/(\d+)/i);
      return m ? { url: `https://aetherhub.com/Deck/MtgaDeckExport/${m[1]}`, kind: 'text' } : null;
    },
    fallbackHint: 'On AetherHub, click "Export -> MTG Arena" and paste below.',
  },
  {
    source: 'mtgdecks_url',
    label: 'MTGDecks',
    hostRegex: /(^|\.)mtgdecks\.net$/i,
    buildExportUrl(u) {
      return { url: `${u.origin}${u.pathname.replace(/\/$/, '')}/txt`, kind: 'text' };
    },
    fallbackHint: 'On MTGDecks, click "Download -> Arena / Text" and paste below.',
  },
  {
    source: 'scryfall_url',
    label: 'Scryfall Decks',
    hostRegex: /(^|\.)scryfall\.com$/i,
    buildExportUrl(u) {
      const m = u.pathname.match(/\/decks\/([0-9a-f-]{36})/i);
      return m ? { url: `https://api.scryfall.com/decks/${m[1]}/export/json`, kind: 'json' } : null;
    },
    fallbackHint: 'On Scryfall, click "Export -> Text" and paste below.',
  },
  {
    source: 'cubecobra_url',
    label: 'Cube Cobra',
    hostRegex: /(^|\.)cubecobra\.com$/i,
    buildExportUrl(u) {
      const m = u.pathname.match(/\/cube\/(?:overview|list|playtest)\/([^/?#]+)/i);
      return m ? { url: `https://cubecobra.com/cube/api/cubeJSON/${m[1]}`, kind: 'json' } : null;
    },
    fallbackHint: 'On Cube Cobra, open "List -> Export -> Plaintext or Cube Cobra CSV" and upload/paste below.',
  },
  {
    source: 'cubetutor_url',
    label: 'CubeTutor',
    hostRegex: /(^|\.)cubetutor\.com$/i,
    buildExportUrl(u) {
      const m = u.pathname.match(/\/(?:viewcube|cubeblog)\/(\d+)/i);
      return m ? { url: `https://www.cubetutor.com/exportcube/${m[1]}`, kind: 'text' } : null;
    },
    fallbackHint: 'Export your CubeTutor list as .txt or .csv and paste or upload it below.',
  },
];

export async function importDeckFromUrl(
  rawUrl: string,
  opts: DeckImportOptions = {}
): Promise<DeckImportResult> {
  let parsedUrl: URL;
  try {
    parsedUrl = new URL(rawUrl.trim());
  } catch {
    throw new Error('Invalid deck URL.');
  }

  const spec = URL_SITE_SPECS.find((s) => s.hostRegex.test(parsedUrl.hostname));
  const source: ImporterSourceId = spec?.source ?? 'plain_text';
  const format = opts.format ?? 'commander';

  if (spec && opts.featureFlags && opts.featureFlags[spec.source] === false) {
    return {
      source: spec.source,
      deck: { name: `${spec.label} Deck`, format, mainboard: [], commanders: [], sideboard: [], maybeboard: [] },
      warnings: [`Direct ${spec.label} URL importer is currently disabled by server feature flag.`],
      legalityWarnings: [],
      unresolvedLines: [],
      requiresPasteFallback: true,
      fallbackInstructions: spec.fallbackHint,
    };
  }

  const endpoint = spec?.buildExportUrl(parsedUrl);
  if (!spec || !endpoint || !opts.fetchAdapter) {
    return {
      source,
      deck: { name: `${spec?.label ?? 'External'} Deck`, format, mainboard: [], commanders: [], sideboard: [], maybeboard: [] },
      warnings: [`Direct fetch unavailable for ${parsedUrl.hostname} — paste exported deck text directly.`],
      legalityWarnings: [],
      unresolvedLines: [],
      requiresPasteFallback: true,
      fallbackInstructions:
        spec?.fallbackHint ?? 'Export the deck as Plain Text / MTGA / MTGO from that site and paste it below.',
    };
  }

  try {
    const resp = await opts.fetchAdapter(endpoint.url, {
      'User-Agent': 'Playtable/0.2 (+https://playtable.deckstr.gg; deck-importer)',
      Accept: endpoint.kind === 'json' ? 'application/json' : 'text/plain, text/csv, */*',
    });

    if (resp.status < 200 || resp.status >= 300 || !resp.body.trim()) {
      return {
        source: spec.source,
        deck: { name: `${spec.label} Deck`, format, mainboard: [], commanders: [], sideboard: [], maybeboard: [] },
        warnings: [`${spec.label} returned HTTP ${resp.status}. Use one-step paste fallback below.`],
        legalityWarnings: [],
        unresolvedLines: [],
        requiresPasteFallback: true,
        fallbackInstructions: spec.fallbackHint,
      };
    }

    if (endpoint.kind === 'json') {
      const res = importJsonOrCubeDeck(resp.body, { ...opts, forceSource: spec.source });
      return { ...res, source: spec.source };
    }
    const textRes = importFuzzyTextOrOcrDeck(resp.body, {
      ...opts,
      deckName: opts.deckName ?? `${spec.label} Import`,
    });
    return {
      ...textRes,
      source: spec.source,
      isCube: spec.source === 'cubetutor_url' || spec.source === 'cubecobra_url',
    };
  } catch (err: any) {
    return {
      source: spec.source,
      deck: { name: `${spec.label} Deck`, format, mainboard: [], commanders: [], sideboard: [], maybeboard: [] },
      warnings: [`Could not reach ${spec.label} (${err?.message ?? 'offline'}). Paste fallback ready.`],
      legalityWarnings: [],
      unresolvedLines: [],
      requiresPasteFallback: true,
      fallbackInstructions: spec.fallbackHint,
    };
  }
}

// ============================================================================
// UNIVERSAL DISPATCHER (`importUniversalDeck`)
// ============================================================================

export async function importUniversalDeck(
  rawInput: string,
  opts: DeckImportOptions = {}
): Promise<DeckImportResult> {
  const trimmed = rawInput.trim();
  const ext = opts.filename ? opts.filename.split('.').pop()?.toLowerCase() : undefined;

  if (opts.forceSource === 'ocr') {
    return importFuzzyTextOrOcrDeck(trimmed, { ...opts, isOcr: true });
  }
  if (/^https?:\/\/\S+$/i.test(trimmed)) {
    return importDeckFromUrl(trimmed, opts);
  }
  if (ext === 'cod' || /<cockatrice_deck\b/i.test(trimmed)) {
    return importCockatriceCodXml(trimmed, opts);
  }
  if (ext === 'dek' || (/<Deck\b/i.test(trimmed) && /<Cards\b/i.test(trimmed))) {
    return importMtgoDekXml(trimmed, opts);
  }
  if (ext === 'dck' || /^\[(metadata|main|commander)\]/im.test(trimmed) || /^\d+\s+\[[A-Za-z0-9]+:[A-Za-z0-9★-]+\]\s+/m.test(trimmed)) {
    return importXmageOrForgeDck(trimmed, opts);
  }
  if (ext === 'json' || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
    return importJsonOrCubeDeck(trimmed, opts);
  }
  if (
    ext === 'csv' ||
    opts.csvMapping ||
    (/^"?(Name|Card Name|Quantity|Count|Tradelist Count)"?\s*,/i.test(trimmed) && trimmed.includes('\n'))
  ) {
    return importCsvDeck(trimmed, opts);
  }
  if (opts.forceSource === 'cube') {
    const res = importFuzzyTextOrOcrDeck(trimmed, { ...opts, format: 'custom' });
    return { ...res, source: 'cube', isCube: true };
  }

  return importFuzzyTextOrOcrDeck(trimmed, opts);
}

// ============================================================================
// CUBE POOL DEALER (Sealed / Draft Booster Pools or Battlebox Shared Pool)
// ============================================================================

export function dealCubePoolsToSeats(
  cubeDeck: ImportedDeckPayload,
  seatIds: readonly string[],
  cardsPerSeat: number,
  rng: RngProvider
): Record<string, ImportedDeckPayload> {
  const expanded: CardDefinition[] = [];
  for (const entry of cubeDeck.mainboard) {
    for (let i = 0; i < entry.quantity; i++) {
      expanded.push(entry.card);
    }
  }
  // Fisher-Yates shuffle with CSPRNG
  for (let i = expanded.length - 1; i > 0; i--) {
    const j = rng.nextInt(i + 1);
    const tmp = expanded[i];
    expanded[i] = expanded[j];
    expanded[j] = tmp;
  }

  const dealt: Record<string, ImportedDeckPayload> = {};
  let cursor = 0;
  for (const sid of seatIds) {
    const slice = expanded.slice(cursor, cursor + cardsPerSeat);
    cursor += cardsPerSeat;
    const grouped = new Map<string, { card: CardDefinition; quantity: number }>();
    for (const c of slice) {
      const k = c.name.toLowerCase();
      const ex = grouped.get(k);
      if (ex) ex.quantity++;
      else grouped.set(k, { card: c, quantity: 1 });
    }
    dealt[sid] = {
      name: `${cubeDeck.name} (Sealed Pool)`,
      format: 'custom',
      mainboard: Array.from(grouped.values()),
      commanders: [],
      sideboard: [],
      maybeboard: [],
    };
  }
  return dealt;
}

// ============================================================================
// MULTI-FORMAT DECK EXPORTER ("Sharing Out")
// ============================================================================

export type DeckExportFormat =
  | 'plain'
  | 'arena'
  | 'mtgo_txt'
  | 'mtgo_dek'
  | 'cockatrice_cod'
  | 'xmage_dck'
  | 'csv'
  | 'json';

function escapeXml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function exportDeckToFormat(deck: ImportedDeckPayload, target: DeckExportFormat): string {
  if (target === 'json') {
    return JSON.stringify(deck, null, 2);
  }

  if (target === 'csv') {
    const lines = ['"Quantity","Name","Set Code","Collector Number","Foil","Section"'];
    for (const c of deck.commanders) {
      lines.push(
        `"1","${c.name.replace(/"/g, '""')}","${c.setCode ?? ''}","${c.collectorNumber ?? ''}","${c.foil ? 'foil' : ''}","Commander"`
      );
    }
    for (const e of deck.mainboard) {
      lines.push(
        `"${e.quantity}","${e.card.name.replace(/"/g, '""')}","${e.card.setCode ?? ''}","${e.card.collectorNumber ?? ''}","${e.card.foil ? 'foil' : ''}","Mainboard"`
      );
    }
    for (const e of deck.sideboard) {
      lines.push(
        `"${e.quantity}","${e.card.name.replace(/"/g, '""')}","${e.card.setCode ?? ''}","${e.card.collectorNumber ?? ''}","${e.card.foil ? 'foil' : ''}","Sideboard"`
      );
    }
    return lines.join('\n');
  }

  if (target === 'mtgo_dek') {
    const lines = ['<?xml version="1.0" encoding="utf-8"?>', '<Deck xmlns:xsd="http://www.w3.org/2001/XMLSchema">'];
    for (const e of deck.mainboard) {
      lines.push(`  <Cards CatID="0" Quantity="${e.quantity}" Sideboard="false" Name="${escapeXml(e.card.name)}" />`);
    }
    for (const c of deck.commanders) {
      lines.push(`  <Cards CatID="0" Quantity="1" Sideboard="true" Name="${escapeXml(c.name)}" />`);
    }
    for (const e of deck.sideboard) {
      lines.push(`  <Cards CatID="0" Quantity="${e.quantity}" Sideboard="true" Name="${escapeXml(e.card.name)}" />`);
    }
    lines.push('</Deck>');
    return lines.join('\n');
  }

  if (target === 'cockatrice_cod') {
    const lines = [
      '<?xml version="1.0" encoding="UTF-8"?>',
      '<cockatrice_deck version="1">',
      `  <deckname>${escapeXml(deck.name)}</deckname>`,
      '  <zone name="main">',
    ];
    for (const e of deck.mainboard) {
      lines.push(`    <card number="${e.quantity}" name="${escapeXml(e.card.name)}"/>`);
    }
    lines.push('  </zone>');
    if (deck.commanders.length > 0 || deck.sideboard.length > 0) {
      lines.push('  <zone name="side">');
      for (const c of deck.commanders) {
        lines.push(`    <card number="1" name="${escapeXml(c.name)}"/>`);
      }
      for (const e of deck.sideboard) {
        lines.push(`    <card number="${e.quantity}" name="${escapeXml(e.card.name)}"/>`);
      }
      lines.push('  </zone>');
    }
    lines.push('</cockatrice_deck>');
    return lines.join('\n');
  }

  if (target === 'xmage_dck') {
    const lines = ['[metadata]', `Name=${deck.name}`];
    if (deck.commanders.length > 0) {
      lines.push('[Commander]');
      for (const c of deck.commanders) {
        const setTag = c.setCode && c.collectorNumber ? `[${c.setCode}:${c.collectorNumber}] ` : '';
        lines.push(`1 ${setTag}${c.name}`);
      }
    }
    lines.push('[Main]');
    for (const e of deck.mainboard) {
      const setTag = e.card.setCode && e.card.collectorNumber ? `[${e.card.setCode}:${e.card.collectorNumber}] ` : '';
      lines.push(`${e.quantity} ${setTag}${e.card.name}`);
    }
    if (deck.sideboard.length > 0) {
      lines.push('[Sideboard]');
      for (const e of deck.sideboard) {
        lines.push(`SB: ${e.quantity} ${e.card.name}`);
      }
    }
    return lines.join('\n');
  }

  if (target === 'mtgo_txt') {
    const lines: string[] = [];
    for (const e of deck.mainboard) lines.push(`${e.quantity} ${e.card.name}`);
    if (deck.commanders.length > 0 || deck.sideboard.length > 0) {
      lines.push('', 'Sideboard');
      for (const c of deck.commanders) lines.push(`1 ${c.name}`);
      for (const e of deck.sideboard) lines.push(`${e.quantity} ${e.card.name}`);
    }
    return lines.join('\n');
  }

  // 'arena' or 'plain'
  const lines: string[] = [];
  if (deck.commanders.length > 0) {
    lines.push('Commander');
    for (const c of deck.commanders) {
      const suffix = target === 'arena' && c.setCode && c.collectorNumber ? ` (${c.setCode}) ${c.collectorNumber}` : '';
      lines.push(`1 ${c.name}${suffix}`);
    }
    lines.push('');
  }
  lines.push('Deck');
  for (const e of deck.mainboard) {
    const suffix =
      target === 'arena' && e.card.setCode && e.card.collectorNumber
        ? ` (${e.card.setCode}) ${e.card.collectorNumber}`
        : '';
    lines.push(`${e.quantity} ${e.card.name}${suffix}`);
  }
  if (deck.sideboard.length > 0) {
    lines.push('', 'Sideboard');
    for (const e of deck.sideboard) {
      const suffix =
        target === 'arena' && e.card.setCode && e.card.collectorNumber
          ? ` (${e.card.setCode}) ${e.card.collectorNumber}`
          : '';
      lines.push(`${e.quantity} ${e.card.name}${suffix}`);
    }
  }
  return lines.join('\n');
}

export { parsePlainTextDeck };
